// client/src/pages/ClientDetailPage.jsx
//
// Single-client detail view. Sections:
//   - Header: name + lifetime totals
//   - Edit panel: name, email, phone, notes — inline editable
//   - Addresses: list with default flag, add/edit/remove
//   - Activity: tabs for Presentations / POs / Quote Requests with clickable
//     rows that navigate into each
//
// Soft delete (archive) is on the header. Archived clients hide from the
// list page but the detail page is still reachable by URL — old presentations
// linked to them keep their reference intact.

import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

export default function ClientDetailPage({ clientId, getToken, navigate, showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await api.withToken(token).getClient(clientId);
      setData(res);
    } catch (e) {
      showToast?.('Failed to load client: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId, getToken, showToast]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div style={{ padding:40,textAlign:'center',color:'#64748B' }}>Loading…</div>;
  }
  if (!data) {
    return <div style={{ padding:40,textAlign:'center',color:'#64748B' }}>Client not found.</div>;
  }

  const { client, addresses = [], presentations = [], purchase_orders = [], quote_requests = [], totals } = data;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 18 }}>
        <button onClick={() => navigate?.('clients')}
          style={{ background:'none',border:'none',color:'#64748B',fontSize:12,cursor:'pointer',fontFamily:'inherit',padding:0 }}>
          ← Back to clients
        </button>
      </div>

      <HeaderSection client={client} totals={totals}
        onArchive={async () => {
          if (!window.confirm(`Archive ${client.name}? They'll disappear from autocomplete; existing presentations/POs keep their reference.`)) return;
          try {
            const token = await getToken();
            await api.withToken(token).archiveClient(client.id);
            showToast?.('Client archived', 'success');
            navigate?.('clients');
          } catch (e) {
            showToast?.('Could not archive: ' + e.message, 'error');
          }
        }}/>

      <ClientEditPanel client={client} getToken={getToken}
        onUpdated={(c) => { setData(d => ({ ...d, client: c })); showToast?.('Saved', 'success'); }}/>

      <AddressesPanel clientId={client.id} addresses={addresses} getToken={getToken}
        onChanged={load} showToast={showToast}/>

      <ActivityPanel
        presentations={presentations}
        purchase_orders={purchase_orders}
        quote_requests={quote_requests}
        navigate={navigate}/>
    </div>
  );
}

// ── Header with name + lifetime totals ─────────────────────────────────────
function HeaderSection({ client, totals, onArchive }) {
  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:24,marginBottom:16 }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:18 }}>
        <div style={{ display:'flex',alignItems:'center',gap:14 }}>
          <div style={{
            width:48,height:48,borderRadius:'50%',background:G,color:'#fff',
            display:'flex',alignItems:'center',justifyContent:'center',
            fontSize:20,fontWeight:600,flexShrink:0,
          }}>
            {(client.name || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize:22,fontWeight:700,color:'#0F172A',letterSpacing:'-0.3px' }}>
              {client.name}
              {client.archived_at && <span style={{ fontSize:11,marginLeft:10,color:'#94A3B8',fontWeight:400 }}>archived</span>}
            </div>
            <div style={{ fontSize:12,color:'#64748B',marginTop:2 }}>
              Added {new Date(client.created_at).toLocaleDateString('en-US', { month:'long',day:'numeric',year:'numeric' })}
            </div>
          </div>
        </div>
        {!client.archived_at && (
          <button onClick={onArchive}
            style={{ background:'none',border:'1px solid #FECACA',borderRadius:7,padding:'7px 14px',
              fontSize:11,color:'#DC2626',cursor:'pointer',fontFamily:'inherit',fontWeight:500 }}>
            Archive
          </button>
        )}
      </div>

      <div style={{ display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:16 }}>
        <Stat label="Presentations" value={totals?.presentations_count ?? 0}/>
        <Stat label="POs" value={totals?.pos_count ?? 0}/>
        <Stat label="Quote requests" value={totals?.quotes_count ?? 0}/>
        <Stat label="PO value" value={`$${(totals?.po_value ?? 0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`}/>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize:10,color:'#94A3B8',textTransform:'uppercase',letterSpacing:'0.5px',fontWeight:600,marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:18,fontWeight:600,color:'#0F172A' }}>{value}</div>
    </div>
  );
}

// ── Edit name / email / phone / notes ──────────────────────────────────────
function ClientEditPanel({ client, getToken, onUpdated }) {
  const [name,  setName]  = useState(client.name);
  const [email, setEmail] = useState(client.email || '');
  const [phone, setPhone] = useState(client.phone || '');
  const [notes, setNotes] = useState(client.notes || '');
  const [saving, setSaving] = useState(false);

  // Track whether any field differs from the loaded client. Used to dim the
  // Save button when nothing's been changed.
  const dirty = name !== client.name
    || email !== (client.email || '')
    || phone !== (client.phone || '')
    || notes !== (client.notes || '');

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const token = await getToken();
      const updated = await api.withToken(token).updateClient(client.id, {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        notes: notes.trim() || null,
      });
      onUpdated(updated);
    } catch (e) {
      console.error('updateClient', e);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 11px',
    fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box',
  };

  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:24,marginBottom:16 }}>
      <div style={{ fontSize:13,fontWeight:600,color:'#0F172A',marginBottom:14 }}>Details</div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
        <div>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Name</label>
          <input value={name} onChange={e => setName(e.target.value)} style={inputStyle}/>
        </div>
        <div>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" style={inputStyle}/>
        </div>
      </div>
      <div style={{ marginBottom:12 }}>
        <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Phone</label>
        <input value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle}/>
      </div>
      <div style={{ marginBottom:12 }}>
        <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
          placeholder="Anything you want to remember about this client…"
          style={{ ...inputStyle,resize:'vertical',minHeight:64,fontFamily:'inherit' }}/>
      </div>
      <div style={{ display:'flex',justifyContent:'flex-end' }}>
        <button onClick={save} disabled={!dirty || saving}
          style={{ background:!dirty ? '#E2E8F0' : G,border:'none',borderRadius:7,padding:'8px 18px',
            color:'#fff',fontSize:12,fontWeight:500,
            cursor:(!dirty || saving) ? 'not-allowed' : 'pointer',fontFamily:'inherit' }}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ── Addresses ──────────────────────────────────────────────────────────────
function AddressesPanel({ clientId, addresses, getToken, onChanged, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);  // address being edited, if any

  const startNew = () => { setEditing(null); setShowForm(true); };
  const startEdit = (a) => { setEditing(a); setShowForm(true); };

  const archive = async (id) => {
    if (!window.confirm('Remove this address?')) return;
    try {
      const token = await getToken();
      await api.withToken(token).archiveClientAddress(clientId, id);
      onChanged();
    } catch (e) { showToast?.(e.message || 'Failed', 'error'); }
  };

  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:24,marginBottom:16 }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14 }}>
        <div style={{ fontSize:13,fontWeight:600,color:'#0F172A' }}>Addresses</div>
        {!showForm && (
          <button onClick={startNew}
            style={{ background:'#fff',border:`1px solid ${G}`,borderRadius:7,padding:'6px 14px',
              fontSize:11,color:G,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
            + Add address
          </button>
        )}
      </div>

      {addresses.length === 0 && !showForm && (
        <div style={{ fontSize:12,color:'#94A3B8',padding:'12px 0' }}>
          No addresses saved. Add one to auto-populate PO ship-to.
        </div>
      )}

      {addresses.map(a => (
        <div key={a.id} style={{ border:'1px solid #E2E8F0',borderRadius:9,padding:14,marginBottom:10,
          background: a.is_default ? `${G}08` : '#fff' }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6 }}>
            <div>
              <div style={{ fontSize:13,fontWeight:600,color:'#0F172A' }}>
                {a.label || 'Address'}
                {a.is_default && <span style={{ fontSize:10,color:G,marginLeft:8,padding:'2px 7px',background:`${G}18`,borderRadius:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px' }}>Default</span>}
              </div>
            </div>
            <div style={{ display:'flex',gap:6 }}>
              <button onClick={() => startEdit(a)}
                style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'3px 10px',
                  fontSize:11,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>Edit</button>
              <button onClick={() => archive(a.id)}
                style={{ background:'none',border:'1px solid #FECACA',borderRadius:6,padding:'3px 10px',
                  fontSize:11,color:'#DC2626',cursor:'pointer',fontFamily:'inherit' }}>Remove</button>
            </div>
          </div>
          <div style={{ fontSize:12,color:'#475569',lineHeight:1.55 }}>
            {a.ship_to_contact_name && <div>{a.ship_to_contact_name}</div>}
            {a.line1 && <div>{a.line1}</div>}
            {a.line2 && <div>{a.line2}</div>}
            <div>{[a.city, a.state, a.postal_code].filter(Boolean).join(', ')}{a.country && a.country !== 'US' ? `, ${a.country}` : ''}</div>
            {a.ship_to_phone && <div style={{ marginTop:3 }}>📞 {a.ship_to_phone}</div>}
          </div>
        </div>
      ))}

      {showForm && (
        <AddressForm
          clientId={clientId}
          existing={editing}
          getToken={getToken}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); onChanged(); }}/>
      )}
    </div>
  );
}

function AddressForm({ clientId, existing, getToken, onCancel, onSaved }) {
  const [f, setF] = useState({
    label: existing?.label || '',
    ship_to_contact_name: existing?.ship_to_contact_name || '',
    ship_to_phone: existing?.ship_to_phone || '',
    line1: existing?.line1 || '',
    line2: existing?.line2 || '',
    city: existing?.city || '',
    state: existing?.state || '',
    postal_code: existing?.postal_code || '',
    country: existing?.country || 'US',
    is_default: existing?.is_default || false,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      if (existing) {
        await api.withToken(token).updateClientAddress(clientId, existing.id, f);
      } else {
        await api.withToken(token).createClientAddress(clientId, f);
      }
      onSaved();
    } catch (e) {
      console.error('save address', e);
      setSaving(false);
    }
  };

  const inputStyle = {
    width:'100%',border:'1px solid #E2E8F0',borderRadius:6,padding:'7px 10px',
    fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box',
  };

  return (
    <div style={{ border:'1px solid #E2E8F0',borderRadius:9,padding:14,marginTop:10,background:'#F8FAFC' }}>
      <div style={{ fontSize:12,fontWeight:600,color:'#0F172A',marginBottom:10 }}>
        {existing ? 'Edit address' : 'New address'}
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8 }}>
        <div>
          <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Label</label>
          <input value={f.label} onChange={e => upd('label', e.target.value)}
            placeholder="HQ, Warehouse, etc." style={inputStyle}/>
        </div>
        <div>
          <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Country</label>
          <select value={f.country} onChange={e => upd('country', e.target.value)} style={inputStyle}>
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8 }}>
        <div>
          <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Contact name</label>
          <input value={f.ship_to_contact_name} onChange={e => upd('ship_to_contact_name', e.target.value)}
            placeholder="Receiving contact" style={inputStyle}/>
        </div>
        <div>
          <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Phone</label>
          <input value={f.ship_to_phone} onChange={e => upd('ship_to_phone', e.target.value)}
            placeholder="For carrier" style={inputStyle}/>
        </div>
      </div>
      <div style={{ marginBottom:8 }}>
        <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Street</label>
        <input value={f.line1} onChange={e => upd('line1', e.target.value)} style={inputStyle}/>
      </div>
      <div style={{ marginBottom:8 }}>
        <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Apt / suite</label>
        <input value={f.line2} onChange={e => upd('line2', e.target.value)} style={inputStyle}/>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:8,marginBottom:10 }}>
        <div>
          <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>City</label>
          <input value={f.city} onChange={e => upd('city', e.target.value)} style={inputStyle}/>
        </div>
        <div>
          <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>{f.country === 'CA' ? 'Prov' : 'State'}</label>
          <input value={f.state} onChange={e => upd('state', e.target.value)} style={inputStyle}/>
        </div>
        <div>
          <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>ZIP</label>
          <input value={f.postal_code} onChange={e => upd('postal_code', e.target.value)} style={inputStyle}/>
        </div>
      </div>
      <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#374151',cursor:'pointer',marginBottom:12 }}>
        <input type="checkbox" checked={f.is_default} onChange={e => upd('is_default', e.target.checked)}/>
        Use as default address (auto-fills PO ship-to)
      </label>
      <div style={{ display:'flex',justifyContent:'flex-end',gap:8 }}>
        <button onClick={onCancel} disabled={saving}
          style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:7,padding:'7px 14px',fontSize:11,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>Cancel</button>
        <button onClick={save} disabled={saving}
          style={{ background:G,border:'none',borderRadius:7,padding:'7px 16px',fontSize:11,color:'#fff',fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
          {saving ? 'Saving…' : (existing ? 'Save changes' : 'Add address')}
        </button>
      </div>
    </div>
  );
}

// ── Activity tabs ──────────────────────────────────────────────────────────
function ActivityPanel({ presentations, purchase_orders, quote_requests, navigate }) {
  const [tab, setTab] = useState('presentations');
  const lists = { presentations, purchase_orders, quote_requests };
  const counts = {
    presentations: presentations.length,
    purchase_orders: purchase_orders.length,
    quote_requests: quote_requests.length,
  };

  return (
    <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,padding:0,overflow:'hidden' }}>
      <div style={{ display:'flex',borderBottom:'1px solid #F1F5F9' }}>
        {[
          ['presentations',   'Presentations'],
          ['purchase_orders', 'Purchase Orders'],
          ['quote_requests',  'Quote Requests'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ flex:1,padding:'14px 0',background:'none',border:'none',
              borderBottom: tab === k ? `2px solid ${G}` : '2px solid transparent',
              color: tab === k ? '#0F172A' : '#64748B',
              fontSize:12,fontWeight: tab === k ? 600 : 500,
              cursor:'pointer',fontFamily:'inherit' }}>
            {label} <span style={{ color:'#94A3B8',fontSize:11 }}>({counts[k]})</span>
          </button>
        ))}
      </div>
      <div>
        {lists[tab].length === 0 ? (
          <div style={{ padding:'32px 24px',textAlign:'center',color:'#94A3B8',fontSize:12 }}>
            None yet.
          </div>
        ) : (
          lists[tab].map(row => <ActivityRow key={row.id} kind={tab} row={row} navigate={navigate}/>)
        )}
      </div>
    </div>
  );
}

function ActivityRow({ kind, row, navigate }) {
  // Different fields per kind. Render a single line with a primary title +
  // status pill + date.
  // Navigation targets follow existing hash-routing convention:
  //   - PO has a per-id detail page (pos/<id>)
  //   - Presentations have no per-id route, so we hand off the id via
  //     sessionStorage (see Presentations.jsx's pending_presentation_open
  //     effect) and it opens straight into the editor for that row.
  //   - Quote requests only have a list page.
  const date = new Date(row.created_at).toLocaleDateString('en-US', { month:'short',day:'numeric',year:'numeric' });
  let title, statusLabel, target, onOpen;
  if (kind === 'presentations') {
    title = row.name || 'Untitled presentation';
    statusLabel = row.archived ? 'Archived' : (row.status || 'draft');
    target = 'presentations';
    // No per-id route exists for presentations (unlike POs below), so stash
    // the id for Presentations.jsx to pick up on mount and jump straight
    // into editing this row instead of landing on the full list.
    onOpen = () => {
      try { sessionStorage.setItem('pending_presentation_open', JSON.stringify({ id: row.id })); } catch {}
      navigate?.(target);
    };
  } else if (kind === 'purchase_orders') {
    title = row.po_number || 'PO';
    statusLabel = row.status || 'draft';
    target = `pos/${row.id}`;
    onOpen = () => navigate?.(target);
  } else {
    title = 'Quote request';
    statusLabel = row.status || 'new';
    target = 'quotes';
    onOpen = () => navigate?.(target);
  }

  return (
    <div onClick={onOpen}
      style={{ display:'flex',alignItems:'center',padding:'12px 18px',
        borderBottom:'1px solid #F8FAFC',cursor:'pointer',gap:12 }}>
      <div style={{ flex:1,minWidth:0 }}>
        <div style={{ fontSize:13,fontWeight:500,color:'#0F172A',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>{title}</div>
        <div style={{ fontSize:11,color:'#94A3B8',marginTop:2 }}>{date}</div>
      </div>
      {row.total != null && (
        <div style={{ fontSize:13,fontWeight:600,color:'#374151' }}>
          ${Number(row.total).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
        </div>
      )}
      <div style={{ fontSize:10,fontWeight:600,padding:'3px 10px',borderRadius:10,
        background:'#F1F5F9',color:'#475569',textTransform:'uppercase',letterSpacing:'0.4px' }}>
        {statusLabel}
      </div>
    </div>
  );
}
